/**
 * FE-side region → IANA timezone map. Mirrors the backend's
 * `Zaveato.Domain.Common.RegionDefaults.TimeZoneFor(regionCode)`.
 *
 * SRP: THE single source of truth for FE code that needs to render a
 * timestamp / day-of-week in a shop's OR provider's OWN region wall-
 * clock (checkout same-day cutoff, WL storefront opening-hours "today"
 * highlight, admin/provider order lists rendered in shop TZ).
 *
 * OCP: adding a new region = one line here. Callers all read via
 * `getTimeZoneIdForRegion(regionCode)` and pass the result as
 * `timeZone: '...'` to `Intl.DateTimeFormat`.
 *
 * Deliberately kept separate from `addressFormats.ts` (per-region
 * form-field config) — timezone is a rendering concern, address
 * format is a form concern, no coupling between them.
 */
import type { RegionCode } from '@/config/hosts'

/** IANA timezone id per region — mirrors backend RegionDefaults.Map. */
const REGION_TIMEZONES: Record<RegionCode, string> = {
  GB: 'Europe/London',
  PK: 'Asia/Karachi',
  AE: 'Asia/Dubai',
  SA: 'Asia/Riyadh',
}

/** Fallback IANA id when a region code is unknown / null / missing. */
export const FALLBACK_TIMEZONE_ID = 'Europe/London'

/**
 * Resolve a region code to its IANA timezone id. Case-insensitive,
 * whitespace-tolerant. Unknown / null / empty → `FALLBACK_TIMEZONE_ID`
 * (matches backend `RegionDefaults.FallbackRegion` = "GB").
 */
export function getTimeZoneIdForRegion(regionCode: string | null | undefined): string {
  const normalised = (regionCode ?? '').trim().toUpperCase() as RegionCode
  return REGION_TIMEZONES[normalised] ?? FALLBACK_TIMEZONE_ID
}

/**
 * Current day-of-week (0=Sunday..6=Saturday) in the given region's
 * local wall-clock. Use in place of `new Date().getDay()` whenever
 * you're evaluating a shop-scoped state (opening-hours "today" row,
 * WL storefront "we're open now" pill).
 *
 * Implementation reads the weekday via `Intl.DateTimeFormat` with an
 * explicit `timeZone`, so it works regardless of the viewer's own
 * browser timezone.
 */
export function getRegionDayOfWeek(regionCode: string | null | undefined, at: Date = new Date()): number {
  const tz = getTimeZoneIdForRegion(regionCode)
  const weekday = new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'short' }).format(at)
  // "Sun" -> 0, "Mon" -> 1, ..., "Sat" -> 6 — matches JS Date.getDay().
  const map: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 }
  return map[weekday] ?? new Date().getDay()
}

/**
 * Current wall-clock hour + minute in the given region. Use for
 * cutoff checks ("is now past the same-day-delivery cutoff for this
 * shop"). Returns an Intl.DateTimeFormat-parsed 24-hour pair.
 */
export function getRegionHourMinute(
  regionCode: string | null | undefined,
  at: Date = new Date(),
): { hour: number; minute: number } {
  const tz = getTimeZoneIdForRegion(regionCode)
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: tz,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(at)
  const hour = parseInt(parts.find((p) => p.type === 'hour')?.value ?? '0', 10)
  const minute = parseInt(parts.find((p) => p.type === 'minute')?.value ?? '0', 10)
  return { hour, minute }
}
